import React, { useMemo, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { CheckCheck } from 'lucide-react';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/ru';

import {
  selectUnreadGroups,
  selectReadGroups,
  selectPendingGroups,
  selectIsLoading,
  selectUnreadTaskCount,
  selectHasAnyGroups,
} from '../../../../redux/state-areas/notifications/notificationSelectors';
import {
  fetchNotifications,
  markAllRead,
} from '../../../../redux/state-areas/notifications/notificationActions';

import UserAvatar from '../../atoms/ticket-drawer/UserAvatar';
import HtmlRenderer from '../../organisms/HtmlRenderer';
import EmptyState from '../../atoms/empty-state/EmptyState';

dayjs.extend(relativeTime);

const MAX_ITEMS = 12;

function dayjsLocale(intlLocale) {
  return (intlLocale ?? 'en').split('-')[0];
}

function getEventDescription(event) {
  const raw = event.action === 'taskAssignment' ? event.title : event.description;
  if (!raw) return null;
  if (event.action === 'taskUpdate' && event.assignor?.fullName) {
    return raw.replace(new RegExp(`^${event.assignor.fullName}\\s*`), '');
  }
  return raw;
}

function flattenGroups(groups, unreadOnly) {
  const items = [];
  for (const group of groups) {
    for (const event of group.events) {
      if (unreadOnly && event.assignee?.isViewed) continue;
      items.push({
        ...event,
        _taskTitle: group.taskTitle,
        _titleIdentifier: group.titleIdentifier,
        _isChatThread: group.isChatThread,
      });
    }
  }
  return items.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
}

const SkeletonItem = () => (
  <div className="flex items-start gap-2.5 px-3.5 py-2.5 border-b border-white/5">
    <div className="w-[20px] h-[20px] rounded-full bg-white/10 animate-pulse flex-shrink-0 mt-0.5" />
    <div className="flex-1 space-y-1.5">
      <div className="h-2.5 bg-white/10 rounded animate-pulse w-4/5" />
      <div className="h-2 bg-white/10 rounded animate-pulse w-2/5" />
    </div>
  </div>
);

const EventRow = ({ event, locale, isLast, onClick }) => {
  const isUnread = !event.assignee?.isViewed;
  const description = getEventDescription(event);
  const timeAgo = (() => {
    try { return dayjs(event.createdAt).locale(locale).fromNow(true); } catch { return ''; }
  })();

  return (
    <div
      className={`flex items-start gap-2 px-3 py-2.5 cursor-pointer transition-colors ${!isLast ? 'border-b border-white/5' : ''}`}
      style={{ background: isUnread ? 'rgba(99,102,241,0.12)' : 'transparent' }}
      onClick={onClick}
      onMouseEnter={e => { e.currentTarget.style.background = isUnread ? 'rgba(99,102,241,0.2)' : 'rgba(255,255,255,0.06)'; }}
      onMouseLeave={e => { e.currentTarget.style.background = isUnread ? 'rgba(99,102,241,0.12)' : 'transparent'; }}
    >
      {event.assignor ? (
        <UserAvatar
          src={event.assignor.avi}
          fullName={event.assignor.fullName}
          userId={event.assignor._id}
          classNames="w-[20px] h-[20px] rounded-full flex-shrink-0 mt-0.5"
        />
      ) : (
        <div className="w-[20px] h-[20px] rounded-full bg-white/10 flex-shrink-0 mt-0.5" />
      )}

      <div className="flex-1 min-w-0">
        {description ? (
          <div className="text-[11.5px] leading-[1.4] line-clamp-2 notification-html-white">
            <HtmlRenderer html={description} />
          </div>
        ) : (
          <div className="text-[11.5px] italic" style={{ color: 'rgba(255,255,255,0.35)' }}>
            {event._taskTitle || '—'}
          </div>
        )}
        <div className="flex items-center gap-1 mt-0.5">
          {event._taskTitle && !event._isChatThread && (
            <span className="text-[10.5px] truncate max-w-[120px]" style={{ color: 'rgba(255,255,255,0.35)' }}>
              {event._taskTitle}
            </span>
          )}
          {event._taskTitle && !event._isChatThread && (
            <span style={{ color: 'rgba(255,255,255,0.2)', fontSize: 10 }}>·</span>
          )}
          <span className="text-[10.5px] flex-shrink-0 tabular-nums" style={{ color: 'rgba(255,255,255,0.35)' }}>{timeAgo}</span>
        </div>
      </div>

      {isUnread && (
        <span className="w-1.5 h-1.5 rounded-full flex-shrink-0 mt-1.5" style={{ background: '#818cf8' }} />
      )}
    </div>
  );
};

const NotificationTooltip = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [showUnread, setShowUnread] = useState(false);

  const isLoading = useSelector(selectIsLoading);
  const hasAnyGroups = useSelector(selectHasAnyGroups);
  const unreadCount = useSelector(selectUnreadTaskCount);
  const unreadGroups = useSelector(selectUnreadGroups);
  const pendingGroups = useSelector(selectPendingGroups);
  const readGroups = useSelector(selectReadGroups);

  useEffect(() => {
    if (!hasAnyGroups && !isLoading) {
      dispatch(fetchNotifications());
    }
  }, []);

  const locale = dayjsLocale(intl.locale);

  const events = useMemo(() => {
    const allGroups = [...pendingGroups, ...unreadGroups, ...readGroups];
    return flattenGroups(allGroups, showUnread).slice(0, MAX_ITEMS);
  }, [pendingGroups, unreadGroups, readGroups, showUnread]);

  const handleMarkAll = () => {
    dispatch(markAllRead());
  };

  return (
    <div className="flex flex-col flex-1 min-h-0" style={{ background: '#1c1d21' }}>
      {/* Header */}
      <div className="flex items-center justify-between px-4 pt-3 pb-2.5 flex-shrink-0" style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold" style={{ color: 'rgba(255,255,255,0.85)' }}>
            {intl.formatMessage({ id: 'RECENT_ACTIVITY', defaultMessage: 'Activity' })}
          </span>
          {unreadCount > 0 && (
            <span className="min-w-[18px] h-[18px] px-1 text-white text-[10px] font-semibold rounded-full flex items-center justify-center leading-none" style={{ background: 'rgba(99,102,241,0.9)' }}>
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </div>
        <button
          onClick={handleMarkAll}
          disabled={unreadCount === 0}
          title={intl.formatMessage({ id: 'MARK_ALL_READ', defaultMessage: 'Mark all read' })}
          className="transition-colors p-1 rounded disabled:opacity-30 disabled:cursor-default"
          style={{ color: 'rgba(255,255,255,0.4)' }}
          onMouseEnter={e => e.currentTarget.style.color = 'rgba(255,255,255,0.8)'}
          onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.4)'}
        >
          <CheckCheck size={15} strokeWidth={1.75} />
        </button>
      </div>

      {/* Filter tabs */}
      <div className="flex items-center gap-1.5 px-3.5 py-2 flex-shrink-0" style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
        {[false, true].map((isUnread) => {
          const label = isUnread
            ? intl.formatMessage({ id: 'UNREAD', defaultMessage: 'Unread' })
            : intl.formatMessage({ id: 'ALL', defaultMessage: 'All' });
          const active = showUnread === isUnread;
          return (
            <button
              key={String(isUnread)}
              onClick={() => setShowUnread(isUnread)}
              className="h-6 px-2.5 rounded text-[11px] font-medium transition-all"
              style={{
                background: active ? 'rgba(99,102,241,0.2)' : 'rgba(255,255,255,0.06)',
                border: `1px solid ${active ? 'rgba(99,102,241,0.4)' : 'rgba(255,255,255,0.08)'}`,
                color: active ? 'rgba(165,180,252,0.95)' : 'rgba(255,255,255,0.45)',
              }}
            >
              {label}
            </button>
          );
        })}
      </div>

      {/* Event list */}
      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          Array.from({ length: 5 }).map((_, i) => <SkeletonItem key={i} />)
        ) : events.length === 0 ? (
          <EmptyState
            header={intl.formatMessage({ id: 'YOU_ARE_ALL_CAUGHT_UP', defaultMessage: "You're All Caught Up!" })}
            description={intl.formatMessage({ id: 'SIT_AND_RELAX', defaultMessage: 'Sit back and relax, nothing to see here.' })}
            src="/images/icons/no-mail.svg"
            iconSize="h-24 w-24"
            dark
          />
        ) : (
          events.map((event, idx) => (
            <EventRow
              key={event._id}
              event={event}
              locale={locale}
              isLast={idx === events.length - 1}
              onClick={() => {
                if (event._taskId || event.taskId) {
                  navigate(`/activity`);
                } else {
                  navigate('/activity');
                }
              }}
            />
          ))
        )}
      </div>

      {/* Footer */}
      <button
        onClick={() => navigate('/activity')}
        className="flex items-center justify-center gap-1 px-4 py-2.5 flex-shrink-0 text-[11.5px] transition-colors"
        style={{ borderTop: '1px solid rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.4)' }}
        onMouseEnter={e => { e.currentTarget.style.color = 'rgba(165,180,252,0.9)'; e.currentTarget.style.background = 'rgba(99,102,241,0.06)'; }}
        onMouseLeave={e => { e.currentTarget.style.color = 'rgba(255,255,255,0.4)'; e.currentTarget.style.background = 'transparent'; }}
      >
        {intl.formatMessage({ id: 'VIEW_ALL_ACTIVITY', defaultMessage: 'View all activity' })}
        <svg width="10" height="10" viewBox="0 0 10 10" fill="none" className="opacity-60">
          <path d="M2 5h6M5 2l3 3-3 3" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
    </div>
  );
};

export default NotificationTooltip;
